import { createContext, useContext, useState, ReactNode } from 'react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface ProductContext {
  name?: string;
  category?: string;
  activeFilters?: {
    categories: string[];
    productCount: number;
    totalProducts: number;
  };
  visibleProducts?: Array<{ name: string; category: string }>;
}

interface ChatContextType {
  isOpen: boolean;
  messages: Message[];
  productContext: ProductContext | null;
  setIsOpen: (isOpen: boolean) => void;
  openWithQuery: (query: string, context?: ProductContext) => void;
  openWithProduct: (product: ProductContext) => void;
  addMessage: (message: Message) => void;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  clearMessages: () => void;
  hasIntroMessage: boolean;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const getIntroMessage = (productContext: ProductContext | null): Message => {
  if (productContext) {
    return {
      role: 'assistant',
      content: `Hi! I'm here to help answer your questions about the ${productContext.name}. I can explain how it works, help you understand the results, suggest complementary products, or answer any other questions you have. What would you like to know?`,
      timestamp: Date.now()
    };
  }
  return {
    role: 'assistant',
    content: "Hi! I'm here to help answer your questions about supplements and wellness. What would you like to know?",
    timestamp: Date.now()
  };
};

export function ChatProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [productContext, setProductContext] = useState<ProductContext | null>(null);
  const [messages, setMessages] = useState<Message[]>([getIntroMessage(null)]);

  const hasIntroMessage = messages.length === 1 && messages[0].role === 'assistant';

  const openWithQuery = (query: string, context?: ProductContext) => {
    // Store the query in sessionStorage for the GlobalChatbot to pick up
    sessionStorage.setItem('pendingChatQuery', query);
    
    // Store filter context if provided
    if (context) {
      sessionStorage.setItem('chatFilterContext', JSON.stringify(context));
      setProductContext(context);
    }
    
    setIsOpen(true);
  };

  const openWithProduct = (product: ProductContext) => {
    setProductContext(product);
    const introMsg = getIntroMessage(product);
    setMessages([introMsg]);
    sessionStorage.setItem('chatProductContext', JSON.stringify(product));
    setIsOpen(true);
  };

  const addMessage = (message: Message) => {
    setMessages(prev => [...prev, message]);
  };

  const clearMessages = () => {
    const introMsg = getIntroMessage(productContext);
    setMessages([introMsg]);
    // Clear sessionId when clearing chat
    sessionStorage.removeItem('chatSessionId');
  };

  return (
    <ChatContext.Provider value={{ 
      isOpen, 
      messages,
      productContext,
      setIsOpen, 
      openWithQuery,
      openWithProduct,
      addMessage, 
      setMessages,
      clearMessages,
      hasIntroMessage
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}
